import { useEffect } from "react";

/**
 * Blokada wygaszania ekranu na czas partii.
 *
 * Telefon leżący na kolanach gaśnie po kilkudziesięciu sekundach, a w Kościach
 * czy Catanie potrafi minąć więcej czasu, zanim znów jest twoja kolej — i cudzy
 * ruch ogląda się z ciemnego ekranu albo wcale.
 *
 * Trzymana **tylko w partii**, nie na liście gier: to jedyna rzecz w tej
 * aplikacji, która realnie zjada baterię, a w aucie ładowarka bywa jedna na
 * cztery telefony.
 *
 * Przeglądarka zwalnia blokadę sama, gdy karta idzie w tło albo ekran zostanie
 * zablokowany ręcznie, i **nie oddaje jej po powrocie** — stąd nasłuch
 * `visibilitychange` i ponowna prośba. Bez niego blokada działa do pierwszego
 * schowania aplikacji i nikt nie wie, dlaczego przestała.
 *
 * Brak API (starszy iOS, Firefox bez flagi) nie jest błędem i nie ma
 * komunikatu: użytkownik i tak nic z tym nie zrobi, a `request` odrzuca też
 * wtedy, gdy system odmówi z powodu niskiej baterii.
 */
export function useBlokadaWygaszania(aktywna: boolean): void {
  useEffect(() => {
    if (!aktywna) return;
    if (!("wakeLock" in navigator)) return;

    // `any`, bo `WakeLockSentinel` nie jest w typach TS dla tej wersji lib.dom.
    let blokada: { release: () => Promise<void>; released: boolean } | null = null;
    let porzucona = false;

    const wez = async () => {
      if (porzucona || document.visibilityState !== "visible") return;
      try {
        blokada = await (
          navigator as unknown as {
            wakeLock: { request: (typ: "screen") => Promise<typeof blokada & object> };
          }
        ).wakeLock.request("screen");
      } catch {
        // Odmowa systemu (np. oszczędzanie baterii) — gramy dalej, ekran gaśnie.
      }
    };

    const naPowrot = () => {
      if (document.visibilityState === "visible" && (!blokada || blokada.released)) void wez();
    };

    void wez();
    document.addEventListener("visibilitychange", naPowrot);

    return () => {
      porzucona = true;
      document.removeEventListener("visibilitychange", naPowrot);
      void blokada?.release().catch(() => undefined);
      blokada = null;
    };
  }, [aktywna]);
}
